import React, { useEffect, useLayoutEffect, useRef } from 'react';

import { getFontStack } from '../../config';
import { getSelectionOffsets, readRunsFromDom, renderRunsToDom, setSelectionOffsets } from './editorDom';
import { getBlockStyle } from './textRuns';

import scss from './TextEditOverlay.module.scss';

export const TEXT_UI_ATTR = 'data-ms-text-ui';

const SHORTCUT_STYLES = { b: 'bold', i: 'italic', u: 'underline' };

function TextEditOverlay({
  element,
  viewport,
  selection,
  onSelectionChange,
  onRunsChange,
  onToggleStyle,
  onUndo,
  onRedo,
  onFinish,
}) {
  const editorRef = useRef(null);
  const emittedRunsRef = useRef(null);
  const pointerFocusRef = useRef(false);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const zoom = viewport?.zoom || 1;
  const blockStyle = getBlockStyle(element);

  const renderKey = `${JSON.stringify(blockStyle)}|${zoom}`;
  const renderedKeyRef = useRef(null);
  useLayoutEffect(() => {
    const node = editorRef.current;
    if (!node || (element.runs === emittedRunsRef.current && renderKey === renderedKeyRef.current)) {
      return;
    }
    renderRunsToDom(node, element.runs, blockStyle, zoom);
    emittedRunsRef.current = element.runs;
    renderedKeyRef.current = renderKey;
    if (document.activeElement === node) {
      const length = element.text.length;
      const { start, end } = selectionRef.current;
      setSelectionOffsets(node, Math.min(start, length), Math.min(end, length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.runs, renderKey]);

  useEffect(() => {
    const node = editorRef.current;
    node.focus({ preventScroll: true });
    setSelectionOffsets(node, 0, element.text.length);
    onSelectionChange({ start: 0, end: element.text.length });

    const handleSelectionChange = () => {
      const offsets = getSelectionOffsets(node);
      if (offsets) {
        onSelectionChange(offsets);
      }
    };
    const handlePointerDown = (event) => {
      if (!node.contains(event.target) && !event.target.closest?.(`[${TEXT_UI_ATTR}]`)) {
        onFinish();
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.id]);

  const handleInput = () => {
    const runs = readRunsFromDom(editorRef.current);
    emittedRunsRef.current = runs;
    onRunsChange(runs);
  };

  const handleKeyDown = (event) => {
    event.stopPropagation();
    const mod = event.metaKey || event.ctrlKey;
    const key = event.key.toLowerCase();

    if (event.key === 'Escape') {
      event.preventDefault();
      onFinish();
    } else if (event.key === 'Enter' && !mod) {
      event.preventDefault();
      document.execCommand('insertLineBreak');
    } else if (mod && SHORTCUT_STYLES[key]) {
      event.preventDefault();
      onToggleStyle(SHORTCUT_STYLES[key]);
    } else if (mod && key === 'z') {
      event.preventDefault();
      if (event.shiftKey) {
        onRedo();
      } else {
        onUndo();
      }
    } else if (mod && key === 'y') {
      event.preventDefault();
      onRedo();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    document.execCommand('insertText', false, event.clipboardData.getData('text/plain'));
  };

  const handleFocus = () => {
    if (pointerFocusRef.current) {
      pointerFocusRef.current = false;
      return;
    }
    const { start, end } = selectionRef.current;
    const length = element.text.length;
    setSelectionOffsets(editorRef.current, Math.min(start, length), Math.min(end, length));
  };

  const width = element.width * zoom;
  const height = element.height * zoom;
  const { background } = element;

  return (
    <div
      ref={editorRef}
      className={scss.editor}
      contentEditable
      suppressContentEditableWarning
      role='textbox'
      aria-multiline='true'
      tabIndex={0}
      spellCheck={false}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onKeyUp={(event) => event.stopPropagation()}
      onPaste={handlePaste}
      onDrop={(event) => event.preventDefault()}
      onFocus={handleFocus}
      onPointerDown={() => {
        pointerFocusRef.current = document.activeElement !== editorRef.current;
      }}
      style={{
        left: (viewport?.x || 0) + (element.x - element.width / 2) * zoom,
        top: (viewport?.y || 0) + (element.y - element.height / 2) * zoom,
        width,
        minHeight: height,
        transformOrigin: `${width / 2}px ${height / 2}px`,
        transform: `rotate(${element.rotation}deg)`,
        padding: background.enabled ? (background.padding || 0) * zoom : 0,
        fontFamily: getFontStack(blockStyle.fontFamily),
        fontSize: blockStyle.fontSize * zoom,
        color: blockStyle.fill,
        caretColor: blockStyle.fill,
        lineHeight: element.lineHeight,
        letterSpacing: `${element.letterSpacing * zoom}px`,
        textAlign: element.align,
      }}
    />
  );
}

export default TextEditOverlay;
